import React, { useEffect, useState, useCallback } from 'react';
import { Music, Pause, Loader2, Calendar, CheckCircle2, Download, List, Clock as ClockIcon, AudioLines } from 'lucide-react';
import { toTitleCase } from '../utils/displayNormalization';
import { useLanguage } from '../contexts/LanguageContext';
import BroadcastClockView from './BroadcastClockView';
import ContentMixView from './ContentMixView';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Visual Broadcast Timeline — chronological view of a single broadcast day.
 *
 * Renders ONLY what we observed. Labels:
 *   • MUSIC — confirmed song play (from stream_songs)
 *   • GAP   — empirical silence between consecutive plays (>15s)
 * We never guess "ad / talk / news" — we don't have that capability.
 *
 * Props:
 *   stationId : string (required)
 *   token     : string (required)
 *   date      : 'YYYY-MM-DD' (optional, defaults to yesterday)
 */
const BroadcastTimeline = ({ stationId, token, date }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [minGapSeconds, setMinGapSeconds] = useState(300); // 5 min default
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'clock' | 'content-mix'
  // Switch min_gap together with view mode so the clock sees all
  // ad / DJ-talk gaps (30s threshold) while the List view stays at
  // 5 min to avoid flooding the operator with micro-gaps.
  const switchViewMode = useCallback((next) => {
    setViewMode(next);
    setMinGapSeconds((cur) => {
      if (next === 'clock' && cur > 60) return 30;
      if (next === 'list' && cur === 30) return 300;
      return cur;
    });
  }, []);
  const [targetDate, setTargetDate] = useState(() => {
    if (date) return date;
    const d = new Date();
    d.setDate(d.getDate() - 1); // yesterday by default — complete day
    return d.toISOString().split('T')[0];
  });

  const fetchData = useCallback(async () => {
    if (!stationId || !token) return;
    // v2.13.19 — Skip the timeline fetch when in content-mix mode;
    // that view has its own independent endpoint.
    if (viewMode === 'content-mix') {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      // Clock view needs per-song/per-gap metrics + yesterday's
      // sparkline curve for comparison. List view skips both.
      const includeMetrics = viewMode === 'clock' ? 'true' : 'false';
      const compareYesterday = viewMode === 'clock' ? 'true' : 'false';
      const r = await fetch(
        `${API}/api/broadcast/timeline/${stationId}?date=${targetDate}&min_gap_seconds=${minGapSeconds}&include_metrics=${includeMetrics}&compare_yesterday=${compareYesterday}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(r.ok ? await r.json() : null);
    } finally {
      setLoading(false);
    }
  }, [stationId, token, targetDate, minGapSeconds, viewMode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString('el-GR', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return '—'; }
  };

  const formatDuration = (s) => {
    if (!s) return '—';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec ? `${m}m ${sec}s` : `${m}m`;
  };

  return (
    <div className="space-y-3" data-testid="broadcast-timeline">
      {/* View mode toggle — List vs Clock */}
      <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md p-0.5">
        <button
          onClick={() => switchViewMode('list')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
            viewMode === 'list'
              ? 'bg-amber-500/20 text-amber-300'
              : 'text-white/60 hover:text-white/90'
          }`}
          data-testid="timeline-view-list-btn"
        >
          <List className="w-3 h-3" /> {t('clkView_listView')}
        </button>
        <button
          onClick={() => switchViewMode('clock')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
            viewMode === 'clock'
              ? 'bg-amber-500/20 text-amber-300'
              : 'text-white/60 hover:text-white/90'
          }`}
          data-testid="timeline-view-clock-btn"
        >
          <ClockIcon className="w-3 h-3" /> {t('clkView_clockView')}
        </button>
        {/* v2.13.19 — Content Mix view (music/speech/ads % per hour). */}
        <button
          onClick={() => switchViewMode('content-mix')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
            viewMode === 'content-mix'
              ? 'bg-amber-500/20 text-amber-300'
              : 'text-white/60 hover:text-white/90'
          }`}
          data-testid="timeline-view-content-mix-btn"
        >
          <AudioLines className="w-3 h-3" /> {t('contentMix_tabLabel') || 'Περιεχόμενο'}
        </button>
      </div>

      {/* Timeline-specific controls — hidden in content-mix mode */}
      {viewMode !== 'content-mix' && (
      <>
      {/* Date picker + summary */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5">
          <Calendar className="w-3.5 h-3.5 text-white/50" />
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="bg-transparent text-[12px] text-white/90 outline-none"
            data-testid="timeline-date-picker"
          />
        </label>
        <label
          className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5"
          title={t('i18nB3_hide_gaps_hint')}
        >
          <span className="text-[10px] text-white/40 uppercase tracking-wide">Min gap</span>
          <select
            value={minGapSeconds}
            onChange={(e) => setMinGapSeconds(parseInt(e.target.value, 10))}
            className="bg-transparent text-[12px] text-white/90 outline-none cursor-pointer"
            data-testid="timeline-gap-threshold"
          >
            <option value={30} className="bg-gray-900">30s (clock)</option>
            <option value={60} className="bg-gray-900">1m</option>
            <option value={120} className="bg-gray-900">2m</option>
            <option value={180} className="bg-gray-900">3m</option>
            <option value={300} className="bg-gray-900">5m (default)</option>
            <option value={600} className="bg-gray-900">10m</option>
            <option value={1800} className="bg-gray-900">30m (μόνο talk shows)</option>
          </select>
        </label>
        {data && (
          <div className="flex items-center gap-3 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1">
              <Music className="w-3 h-3 text-emerald-400" />
              {data.music_events} τραγούδια
            </span>
            <span className="inline-flex items-center gap-1">
              <Pause className="w-3 h-3 text-amber-400" />
              {data.gap_events} κενά
            </span>
          </div>
        )}
        {/* Excel export — downloads the currently-selected date's timeline as .xlsx.
            Uses XHR so the JWT is attached and we can handle the blob response. */}
        <button
          onClick={() => {
            if (!stationId || !targetDate) return;
            setExporting(true);
            const xhr = new XMLHttpRequest();
            xhr.open('GET', `${API}/api/broadcast/timeline/${stationId}/export?date=${targetDate}&min_gap_seconds=${minGapSeconds}`);
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.responseType = 'blob';
            xhr.onload = () => {
              setExporting(false);
              if (xhr.status >= 200 && xhr.status < 300) {
                const cd = xhr.getResponseHeader('Content-Disposition') || '';
                const m = /filename="?([^";]+)"?/.exec(cd);
                const filename = m ? m[1] : `timeline_${targetDate}.xlsx`;
                const url = window.URL.createObjectURL(xhr.response);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }
            };
            xhr.onerror = () => setExporting(false);
            xhr.send();
          }}
          disabled={!data || loading || exporting}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
            !data || loading || exporting
              ? 'bg-white/5 text-white/30 cursor-not-allowed'
              : 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30'
          }`}
          data-testid="timeline-export-excel-btn"
          title={t('i18nB3_export_timeline_excel')}
        >
          {exporting ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Εξαγωγή…</>
          ) : (
            <><Download className="w-3 h-3" /> Excel</>
          )}
        </button>
      </div>
      </>
      )}

      {loading && viewMode !== 'content-mix' && (
        <div className="flex items-center justify-center gap-2 text-white/60 text-sm py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> {viewMode === 'clock' ? t('clkView_loadingClocks') : 'Φόρτωση timeline…'}
        </div>
      )}

      {!loading && viewMode !== 'content-mix' && (!data || !data.events?.length) && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-8 text-center">
          <Music className="w-8 h-8 text-white/25 mx-auto mb-2" />
          <div className="text-sm text-white/60">
            Δεν υπάρχουν καταγραφές για αυτή την ημερομηνία.
          </div>
        </div>
      )}

      {/* CONTENT MIX VIEW — independent of timeline data (weekly aggregation) */}
      {viewMode === 'content-mix' && (
        <ContentMixView stationId={stationId} token={token} />
      )}

      {/* CLOCK VIEW */}
      {!loading && data?.events?.length > 0 && viewMode === 'clock' && (
        <BroadcastClockView
          stationId={stationId}
          token={token}
          date={targetDate}
          events={data.events}
          summary={data.executive_summary}
          listenerCurve={data.listener_curve_per_5min}
          listenerCurveYesterday={data.listener_curve_per_5min_yesterday}
          minGapSeconds={minGapSeconds}
        />
      )}

      {/* LIST VIEW */}
      {!loading && data?.events?.length > 0 && viewMode === 'list' && (
        <div className="relative pl-8 space-y-1.5 max-h-[32rem] overflow-y-auto pr-2">
          {/* Vertical line */}
          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-white/10" />

          {data.events.map((e, i) => {
            if (e.type === 'gap') {
              return (
                <div
                  key={`gap-${i}`}
                  className="relative py-1.5 pl-2 flex items-center gap-3"
                  data-testid={`timeline-gap-${i}`}
                >
                  <div className="absolute -left-[18px] w-3.5 h-3.5 rounded-full bg-gray-600 ring-2 ring-gray-900" />
                  <div className="flex-1 flex items-center gap-3 text-[11px] text-white/50 italic">
                    <span className="font-mono tabular-nums">{formatTime(e.start)}</span>
                    <span>— {e.note} ({formatDuration(e.duration_seconds)}) —</span>
                  </div>
                </div>
              );
            }
            // music event
            const displayArtist = toTitleCase(e.artist);
            const displayTitle = toTitleCase(e.title);
            return (
              <div
                key={`music-${i}`}
                className="relative py-1.5 pl-2 flex items-center gap-3 group"
                data-testid={`timeline-music-${i}`}
              >
                <div className="absolute -left-[20px] w-5 h-5 rounded-full bg-emerald-500/20 ring-2 ring-emerald-500/60 flex items-center justify-center">
                  <Music className="w-2.5 h-2.5 text-emerald-300" />
                </div>
                <span className="text-[11px] font-mono tabular-nums text-white/50 w-20 shrink-0">
                  {formatTime(e.start)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    <span className="font-medium">{displayArtist}</span>
                    {displayArtist && <span className="text-white/40 mx-1.5">—</span>}
                    <span className="text-white/85">{displayTitle}</span>
                    {e.fingerprint_verified && (
                      <CheckCircle2
                        className="inline-block w-3 h-3 ml-1.5 text-emerald-400/80 cursor-help"
                        aria-label={t('i18nB3_confirmed_fp')}
                        title={`✓ ${t('i18nB3_confirmed_fp')} (AudD)`}
                      />
                    )}
                    {/* Phase 3 additive marker — song was preserved
                        inside a consolidated BREAK cluster (bridge /
                        jingle). Surface as a tiny amber badge so the
                        operator can audit it. Never suppresses the
                        song row (music-identity invariant). */}
                    {e.part_of_break_cluster === true && (
                      <span
                        className="inline-block ml-1.5 px-1 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/40 align-middle"
                        title="Song preserved inside a consolidated BREAK cluster (Phase 3 annotation)"
                        data-testid={`timeline-music-${i}-in-break-cluster-badge`}
                      >
                        in break
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-white/40 font-mono tabular-nums">
                  {formatDuration(e.visual_duration_seconds || e.duration_seconds)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BroadcastTimeline;
